/* eslint-disable max-lines */
import {
  ConsoleSqlOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FilterOutlined,
  HighlightOutlined,
  HistoryOutlined,
  LineHeightOutlined,
  LockTwoTone,
  PlusOutlined,
  SaveOutlined,
  SearchOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT, POSTHOG_EVENTS } from '@salescore/client-base'
import {
  canViewSheetConfig,
  canViewSheetRecord,
  canViewSheetSchema,
  checkSyncEnabled,
  useClearViewQueryCache,
  useMe,
} from '@salescore/client-common'
import { type HighlightConfigKey, useAppDispatch } from '@salescore/client-redux'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type EltChangeResult,
  getMetricFromKpiFormula,
  type ViewQueryList,
  type ViewQueryRecordNode,
  type ViewUISheet,
} from '@salescore/core'
import { isSharedLinkPresetName, stripSharedLinkPresetNamePrefix } from '@salescore/core'
import { ButtonWithTooltip, organizationHasFeature } from '@salescore/frontend-common'
import { App, Badge, Button, Dropdown, Menu, Popconfirm, Row, Select, Space, Switch } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type Dispatch, type ReactElement, type SetStateAction, useState } from 'react'
import { Trans } from 'react-i18next'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { z } from 'zod'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { dateForDiffHighlightOptions, useHighlightConfig } from '../../../hooks/useHighlightConfig'
import { useIsSavingSettings } from '../../../hooks/useIsSavingSettings'
import { usePageSizeState } from '../../../hooks/usePageSizeState'
import { calculateRowLinesCount, ROW_LINES_COUNT_OPTIONS, useRowLinesCount } from '../../../hooks/useRowLinesCount'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import { useViewStateIsPasting } from '../../../hooks/useViewState'
import { useMoveToRowIndexAndEditModeMutation } from '../../../recoil/cursor'
import { sheetPickedPresetNameAtom } from '../../../recoil/navigation/atoms'
import { useRecordsImpl } from '../../../recoil/records'
import { additionalConfigAtom } from '../../../recoil/records/atoms'
import { useIsSavingState } from '../../../recoil/records/hooks'
import { useAddEmptyRecordMutation } from '../../../recoil/records/mutations/addEmptyRecordMutation'
import { useRecordsAndChangesMutation } from '../../../recoil/records/mutations/recordsAndChangesMutation'
import { useRefetchAggregationQueryResult } from '../../../recoil/records/mutations/useRefetchAggregationQueryResult'
import { useRefetchMutation } from '../../../recoil/records/mutations/useRefetchMutation'
import { changesAtom } from '../../../recoil/recordsHistory'
import { type SalescoreObjectAbility, type ViewAbility, viewsContextAtom } from '../../../recoil/view/atoms'
import { attachMetricToConfigFromFormula } from '../../../recoil/view/attachMetricToKpiConfig'
import {
  configAtom,
  pickAdditionalJoinOns,
  resolveShouldJoinRecordsInApplication,
  useFlattenFilterNodeLeafs,
  useSetUiColumn,
  useViewConfigMutation,
  useViewConfigSheet,
} from '../../../recoil/viewConfig'
import { flatNodes } from '../../../state/nodeUtility'
import { ShareViewButton } from '../../ShareViewButton'
import { buildViewQueryResultsMetadataModalProperties } from '../ViewQueryResultsMetadataModal'
import { ViewUIErrorDrawer } from '../ViewUIErrorDrawer'
import { CompileErrorsButton } from '../ViewUIKpiPivot/CompileErrorsButton'
import { PeriodPicker } from '../ViewUIKpiPivot/PeriodPicker'
import { buildSearchableFieldsFormModalProperties } from './SearchableFieldsFormModal'
import { SearchQueryInput } from './SearchQueryInput'
import { SheetMoreActionsButton } from './SheetMoreActionsButton'
import { buildChangesModalProperties } from './SheetNavigation/ChangesModal'
import { ImportSourceRecordsButton } from './SheetNavigation/ImportButton'
import { SheetPresetsPicker } from './SheetNavigation/SheetPresetsPicker'
import { buildViewQueryFiltersModalProperties } from './SheetNavigation/ViewQueryFiltersModal'
import { buildViewQuerySortersModalProperties } from './SheetNavigation/ViewQuerySortersModal'
import { buildSyncUserPlanNotFoundModalProperties } from './SyncUserPlanNotFoundModal'
import { buildViewDebugModalProperties } from './ViewDebugModal'

export const SheetNavigation = ({
  component,
  recordSaveErrors,
  setRecordSaveErrors,
  columns,
  sheetColumns,
  compiledConfig,
  view,
  setView,
  ability,
  query,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  component: ViewUISheet
  recordSaveErrors: EltChangeResult[]
  setRecordSaveErrors: Dispatch<SetStateAction<EltChangeResult[]>>
  columns: Array<RSheetColumn<RSheetRecordNode>>
  sheetColumns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  setView: (f: (previous: ViewFieldsFragment) => ViewFieldsFragment) => void
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
  // eslint-disable-next-line complexity
}) => {
  const dispatch = useAppDispatch()
  const { message, modal } = App.useApp()
  const { clearTargetViewQueryCaches } = useClearViewQueryCache()
  const { addRootRecord } = useAddEmptyRecordMutation(query, ability)
  const [pageSize, setPageSize] = usePageSizeState()
  const me = useMe()
  const config = useViewConfigSheet()
  const flattenFilterNodeLeafs = useFlattenFilterNodeLeafs(config)
  const flattenAdditionalNodeLeafs = (config.tree === undefined ? [] : pickAdditionalJoinOns(config.tree))
    .flatMap((x) => flatNodes(x))
    .flatMap((x) => x.leafs)

  const shouldJoinRecordsInApplication = resolveShouldJoinRecordsInApplication(me, config)

  const records = useRecordsImpl()
  const { aggregationQueryRecords: aggregationRecords } = useRefetchAggregationQueryResult(compiledConfig, view)
  const savingFlag = useIsSavingState()
  const { setMeta } = useViewConfigMutation()

  const viewsContext = useRecoilValue(viewsContextAtom)
  const viewQueryResult = useViewQueryResult(view)
  const changes = useRecoilValue(changesAtom)
  const moveToRowIndexAndEditModeMutation = useMoveToRowIndexAndEditModeMutation(columns, ability) // 結局ここでrsheet側のstateを操作するしかないか？
  const recordsMutation = useRefetchMutation(view, query)
  const [highlightConfigKey, updateHighlightConfigKeyThunk] = useHighlightConfig(
    (highlightConfigKey: HighlightConfigKey) => {
      void recordsMutation.refetch({ kind: 'reset' }, undefined, { highlightConfigKey })
    },
  )
  const recordsAndChangesMutation = useRecordsAndChangesMutation(compiledConfig, view, query)
  const [rowLinesCount, setRowLinesCount] = useRowLinesCount()
  const calculatedRowLinesCount = calculateRowLinesCount(compiledConfig.ui[0], rowLinesCount)
  const isGoalView = component.isGoalView ?? false
  const isKpiSheet = view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)
  // eslint-disable-next-line sonarjs/todo-tag
  const syncEnabled = checkSyncEnabled(me.myUser) || isGoalView // TODO: だいぶadhocなのでなんとかしたい…
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const previousKpiConfig =
    additionalConfig.previousConfig?.type === 'kpi' ? additionalConfig.previousConfig : undefined
  // 読み取り面 ViewConfigKpiWithMetric は inline date を型から剥がしているため formula.metric 経由で読む
  const periodPickerVisibility =
    previousKpiConfig !== undefined && getMetricFromKpiFormula(previousKpiConfig.formula)?.date !== undefined
  const [presetName] = useRecoilState(sheetPickedPresetNameAtom)
  const invisibleColumns = sheetColumns.filter((x) => x.visible !== true)
  const [isSavingSettings, setIsSavingSettings] = useIsSavingSettings()
  const [errorsDrawerOpen, setErrorsDrawerOpen] = useState(false)
  const currentSavedView = viewsContext.views.find((x) => x.id === view.id) ?? view
  const isPasting = useViewStateIsPasting()
  const setConfig = useSetRecoilState(configAtom)

  // NOTE: NX様専用フラグ(シートで「行追加」ボタンを隠すために使用)
  const enableNxOnlyFeature = organizationHasFeature(me.organization, 'enable_nx_only_feature')

  const showRecordSaveErrorsAlert = (errorSummary?: string) => {
    const viewId = view.id
    const messageKey = `sheet-in-save-error`

    message.error({
      key: messageKey,
      content: (
        <span>
          {errorSummary ?? t('シートの保存中にエラーが発生しました')}
          <Button
            className="ml-2"
            type="text"
            style={{ fontWeight: 'bold' }}
            onClick={() => {
              if (!isKpiSheet) {
                viewsContext.pickView(viewId)
              }
              setErrorsDrawerOpen(true)
              message.destroy(messageKey)
            }}
          >
            {t(`もっと見る`)}
          </Button>
        </span>
      ),
      duration: 5,
    })
  }

  return (
    <div data-e2e="sheet-navigation">
      <Row justify="space-between" className="py-2 pl-2 pr-5" style={{ borderBottom: '1px solid #E1E6E8' }}>
        <Row>
          {!isGoalView && !isKpiSheet && (
            <SheetPresetsPicker
              view={view}
              ability={ability}
              hasChangeToViewSchema={hasChangeToViewSchema}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
          )}
          {/* <FieldButton {...{ sheetNavigationState, viewRecordsState }} /> */}
          <ButtonWithTooltip
            type="text"
            disabled={!ability.canDisplayConfigEdit}
            showTooltip={!ability.canDisplayConfigEdit}
            tooltipTitle={t(`権限がありません`)}
            icon={<FilterOutlined />}
            onClick={() => {
              // sheetNavigationState.action.toggleSiderType('filters')

              const m = modal.confirm(
                buildViewQueryFiltersModalProperties({
                  initialFilterTree: undefined,
                  onAfterFinish: () => {
                    m.destroy()
                  },
                  view,
                  setHasChangeToViewSchema,
                }),
              )
            }}
          >
            {t(`絞り込み`)}
            {flattenFilterNodeLeafs.length + flattenAdditionalNodeLeafs.length > 0 && (
              <span>({flattenFilterNodeLeafs.length + flattenAdditionalNodeLeafs.length})</span>
            )}
          </ButtonWithTooltip>
          <Button
            type="text"
            icon={<SortAscendingOutlined />}
            onClick={() => {
              const m = modal.confirm(
                buildViewQuerySortersModalProperties({
                  hasChanges: changes.length > 0,
                  onClickApply: (sorters) => {
                    setConfig({ ...config, sorters })
                    setHasChangeToViewSchema(true)
                    m.destroy()
                  },
                }),
              )
            }}
          >
            {t(`並び替え`)}
            {query.sorters.length > 0 && <span>({query.sorters.length})</span>}
          </Button>
          {/* <Button type="text" icon={<UnorderedListOutlined />}>
      グルーピング
    </Button> */}
          <Space>
            {isGoalView && (
              <Button
                // type="text"
                icon={<ConsoleSqlOutlined />}
                disabled={viewQueryResult === undefined}
                onClick={() => {
                  modal.confirm(buildViewQueryResultsMetadataModalProperties(compiledConfig, view, query))
                }}
              />
            )}
            {isGoalView && invisibleColumns.length > 0 && (
              <Dropdown
                menu={{
                  items: sheetColumns.map((x) => ({
                    key: `${x.configField?.property.modelName}-${x.configField?.property.propertyName}`,
                    label: <ColumnVisiblityItem column={x} setHasChangeToViewSchema={setHasChangeToViewSchema} />,
                  })),
                }}
              >
                <Badge color="rgb(250,204,21)" count={invisibleColumns.length} overflowCount={999} offset={[-3, 3]}>
                  <Button icon={<EyeInvisibleOutlined />} className="border-yellow-400 text-yellow-500">
                    {t(`非表示`)}
                  </Button>
                </Badge>
              </Dropdown>
            )}
          </Space>
          {!isGoalView && (
            <>
              <Dropdown
                // eslint-disable-next-line sonarjs/deprecation
                overlay={
                  <div className="bg-white p-4 shadow" style={{ width: 240 }}>
                    <div className="mb-3">
                      <Trans>
                        直近の変更を
                        <span className="bg-yellow-100">ハイライト</span>
                      </Trans>
                      <br />
                      <span className="text-xs">※{t(`親レコードのみ`)}</span>
                    </div>
                    <div>
                      <Select
                        className="w-full"
                        value={highlightConfigKey}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onChange={(newKey) => {
                          dispatch(updateHighlightConfigKeyThunk(newKey))
                        }}
                        onMouseDown={() => {
                          if (changes.length > 0) {
                            message.warning(t('設定を変更すると変更情報が失われます。'))
                          }
                        }}
                        options={dateForDiffHighlightOptions}
                      />
                    </div>
                  </div>
                }
                placement="bottomLeft"
                trigger={['hover']}
              >
                <Button type="text" icon={<HighlightOutlined />} />
              </Dropdown>
              <Dropdown
                // eslint-disable-next-line sonarjs/deprecation
                overlay={
                  <Menu title={t(`1行の高さ`)}>
                    {ROW_LINES_COUNT_OPTIONS.map((x) => (
                      <Menu.Item
                        key={x}
                        className={calculatedRowLinesCount === x ? `bg-blue-50` : ``}
                        onClick={() => {
                          setRowLinesCount(x)
                        }}
                      >
                        {t(`{{x}}行分`, { x })}
                        {x === 1 ? t(`(デフォルト)`) : ``}
                      </Menu.Item>
                    ))}
                  </Menu>
                }
                placement="bottomCenter"
              >
                <Button icon={<LineHeightOutlined />} type="text" />
              </Dropdown>
              <Dropdown
                // eslint-disable-next-line sonarjs/deprecation
                overlay={
                  <div className="bg-white p-4 shadow" style={{ width: 300 }}>
                    <div className="mb-4">
                      {t(`一度に取得するレコード数の変更`)}
                      <br />
                      <span className="text-xs">※{t(`表示する行数とは異なる場合があります。`)}</span>
                      <br />
                      <span className="text-xs">※{t(`大きな数を設定した場合、動作が重くなることがあります。`)}</span>
                      <Select
                        className="mt-2 w-full"
                        value={pageSize}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onChange={(value: number) => {
                          setPageSize(value)
                        }}
                        onMouseDown={() => {
                          if (changes.length > 0) {
                            message.warning(t('設定を変更すると変更情報が失われます。'))
                          }
                        }}
                        options={[100, 200, 500, 1000, 2000, 5000].map((x) => ({
                          value: x,
                          label: t(`{{x}}行`, { x }),
                        }))}
                      />
                    </div>
                    <div className="mb-4">
                      {t(`表示する子レコード行数の制限`)}
                      <br />
                      <span className="text-xs">
                        {t(
                          `1つの親レコードに複数の子レコードが紐づくような場合、表示される子レコードの行数を制限することができます。`,
                        )}
                      </span>
                      <Select
                        className="mt-2 w-full"
                        value={component.visibleRowNum ?? -1}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onChange={(visibleRowNumber: number) => {
                          // valueがundefinedのときにonChangeが反応しないため、-1で代替している
                          setMeta({
                            config,
                            newMeta: {
                              visibleRowNum: visibleRowNumber === -1 ? undefined : visibleRowNumber,
                            },
                          })
                          setHasChangeToViewSchema(true)
                        }}
                        options={[-1, 1, 2, 3, 4, 5, 10].map((x) => ({
                          value: x,
                          label: x === -1 ? t(`全て表示`) : t(`{{x}}行`, { x }),
                        }))}
                      />
                    </div>
                    {previousKpiConfig === undefined && (
                      <div className="mb-4">
                        {t(`アプリケーションジョイン`)}
                        <br />
                        <span className="text-xs">
                          {t(
                            `有効化によりシートの読み込み速度の向上が期待されます。ただしオブジェクトを跨いだ絞り込みができなくなります。ブロックのフィルタは可能です。`,
                          )}
                        </span>
                        <div className="flex items-center justify-between align-middle">
                          {/* NOTE: Switchだけだとステータスが若干分かりづらい & レイアウトが難しいと思ったので説明テキストもいれている。しかし冗長だろうか？デザインに悩んだ。 */}
                          <div className="mt-2 text-xs text-gray-500">
                            {shouldJoinRecordsInApplication ? t(`現在有効化されています`) : t(`現在無効化されています`)}
                          </div>
                          <Popconfirm
                            title={
                              <div className="text-yellow-500">
                                {t(`アプリケーションジョイン設定を変更すると`)}
                                <br />
                                {t(`表示されるレコードが変わる可能性があります。`)}
                                <br />
                                {t(`挙動をよく理解した上でお使いください。`)}
                                <br />
                                {t(`変更してよろしいですか？`)}
                              </div>
                            }
                            onConfirm={(e) => {
                              e?.preventDefault()
                              e?.stopPropagation()
                              setMeta({
                                config,
                                newMeta: {
                                  shouldJoinRecordsInApplication: !shouldJoinRecordsInApplication,
                                },
                              })
                              setHasChangeToViewSchema(true)
                            }}
                          >
                            <Switch
                              className="mt-2"
                              checked={shouldJoinRecordsInApplication}
                              onChange={(value, e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                // valueがundefinedのときにonChangeが反応しないため、-1で代替している
                              }}
                            />
                          </Popconfirm>
                        </div>
                      </div>
                    )}
                    <Row justify="space-between" className="mb-2">
                      <Space>
                        <SearchOutlined />
                        {t(`検索項目設定`)}
                      </Space>
                      <Button
                        type="text"
                        style={{
                          color: CONSTANT.colors.primaryColor,
                        }}
                        onClick={() => {
                          const m = modal.confirm(
                            buildSearchableFieldsFormModalProperties({
                              onAfterFinish: () => {
                                m.destroy()
                              },
                              query,
                              setHasChangeToViewSchema,
                            }),
                          )
                        }}
                      >
                        {t(`設定`)}
                      </Button>
                    </Row>
                    <Row justify="space-between" className="mb-2">
                      <Space>
                        <ConsoleSqlOutlined />
                        SQL{t(`表示`)}
                      </Space>
                      <Button
                        type="text"
                        style={{
                          color: CONSTANT.colors.primaryColor,
                        }}
                        disabled={viewQueryResult === undefined}
                        onClick={() => {
                          modal.confirm(buildViewQueryResultsMetadataModalProperties(compiledConfig, view, query))
                        }}
                      >
                        {t(`表示`)}
                      </Button>
                    </Row>
                    {canViewSheetConfig(me) && (
                      <Row justify="space-between" className="mb-2">
                        <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                          <ConsoleSqlOutlined />
                          {t(`設定編集`)}
                        </Space>
                        <Button
                          type="text"
                          style={{
                            color: CONSTANT.colors.primaryColor,
                          }}
                          onClick={() => {
                            const m = modal.confirm(
                              buildViewDebugModalProperties({
                                content: {
                                  config,
                                  additionalConfig,
                                },
                                onAfterFinish: () => {
                                  m.destroy()
                                },
                              }),
                            )
                          }}
                        >
                          {t(`表示`)}
                        </Button>
                      </Row>
                    )}
                    {canViewSheetSchema(me) && (
                      <Row justify="space-between" className="mb-2">
                        <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                          <ConsoleSqlOutlined />
                          {t(`スキーマ表示`)}
                        </Space>
                        <Button
                          type="text"
                          style={{
                            color: CONSTANT.colors.primaryColor,
                          }}
                          onClick={() => {
                            const m = modal.confirm(
                              buildViewDebugModalProperties({
                                content: {
                                  config,
                                  queries: compiledConfig.queries,
                                  ui: compiledConfig.ui,
                                },
                                onAfterFinish: () => {
                                  m.destroy()
                                },
                              }),
                            )
                          }}
                        >
                          {t(`表示`)}
                        </Button>
                      </Row>
                    )}
                    {canViewSheetRecord(me) && (
                      <>
                        <Row justify="space-between" className="mb-2">
                          <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                            <ConsoleSqlOutlined />
                            {t(`レコード表示`)}
                          </Space>
                          <Button
                            type="text"
                            style={{
                              color: CONSTANT.colors.primaryColor,
                            }}
                            onClick={() => {
                              const m = modal.confirm(
                                buildViewDebugModalProperties({
                                  content: {
                                    records,
                                  },
                                  onAfterFinish: () => {
                                    m.destroy()
                                  },
                                }),
                              )
                            }}
                          >
                            {t(`表示`)}
                          </Button>
                        </Row>
                        <Row justify="space-between" className="mb-2">
                          <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                            <ConsoleSqlOutlined />
                            {t(`集計レコード表示`)}
                          </Space>
                          <Button
                            type="text"
                            style={{
                              color: CONSTANT.colors.primaryColor,
                            }}
                            onClick={() => {
                              const m = modal.confirm(
                                buildViewDebugModalProperties({
                                  content: {
                                    aggregationRecords,
                                  },
                                  onAfterFinish: () => {
                                    m.destroy()
                                  },
                                }),
                              )
                            }}
                          >
                            {t(`表示`)}
                          </Button>
                        </Row>
                      </>
                    )}
                  </div>
                }
                placement="bottomLeft"
                trigger={['hover']}
              >
                <Button type="text" icon={<SettingOutlined />} data-e2e="setting-button" />
              </Dropdown>
              {/* ダッシュボード側と位置を揃え、上部ナビゲーションアイコン群の右端 (歯車の右) に配置。KPIシートは対象外 */}
              {!isKpiSheet && (
                <ShareViewButton
                  view={view}
                  parameter={{
                    filterTree: config.filterTree,
                    sorters: config.sorters,
                    fields: config.fields,
                    // config.meta を全量共有。(左端固定列・子レコード表示行数・アプリケーションジョインに加え、カスタム検索SQL の searchQuery・isGoalView も含む)
                    // meta.searchQuery はヘッダーのフリーワード検索 (URL クエリで別途共有) とは別物。
                    meta: config.meta,
                    // コメント経由と保存値を揃えるため [共有リンク] prefix を外して素のプリセット名を保存する
                    sheetPresetName: stripSharedLinkPresetNamePrefix(presetName),
                  }}
                  posthogEvent={POSTHOG_EVENTS.click_shareurl_from_sheet}
                />
              )}
              {/* <div className="text-xs text-gray-400 ml-2" style={{ transform: 'scale(0.7)', opacity: 0.7 }}>
            デバッグ
            <br />
            <Switch
              size="small"
              checked={viewState.debugMode}
              onChange={(value) => {
                viewState.setDebugMode(value)
              }}
            />
          </div> */}
              {periodPickerVisibility && <PeriodPicker />}

              <SearchQueryInput view={view} query={query} setHasChangeToViewSchema={setHasChangeToViewSchema} />
            </>
          )}
        </Row>
        {/* 右側 */}
        <Row>
          <Space>
            {!isGoalView && (
              <ImportSourceRecordsButton
                buttonType="link"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                onSuccess={async () => {
                  // 同期後に古いキャッシュを読まないようにするためにApolloのキャッシュをクリアする
                  const { isDrillDown, prevView, setIsChangedDrillDownModal } = viewsContext
                  const cacheClearViewId = isDrillDown === true ? prevView?.id : view.id
                  clearTargetViewQueryCaches({ viewIds: [cacheClearViewId].filter(isNotNullish) })

                  if (isDrillDown === true && setIsChangedDrillDownModal !== undefined) {
                    setIsChangedDrillDownModal(true)
                  }

                  await recordsMutation.refetch({ kind: 'save' })
                }}
                view={view}
              />
            )}
            <SheetMoreActionsButton
              compiledConfig={compiledConfig}
              view={view}
              query={query}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
            <Button
              disabled={changes.length === 0}
              onClick={() => {
                modal.confirm(buildChangesModalProperties(compiledConfig, view, query))
              }}
              icon={<HistoryOutlined />}
            />
            {recordSaveErrors.length > 0 && (
              <Badge count={recordSaveErrors.length} overflowCount={999} offset={[-3, 3]}>
                <Button
                  danger
                  type="default"
                  icon={<WarningOutlined />}
                  onClick={() => {
                    setErrorsDrawerOpen(true)
                  }}
                />
              </Badge>
            )}
            <CompileErrorsButton
              onClick={() => {
                setErrorsDrawerOpen(true)
              }}
              compiledConfig={compiledConfig}
              view={view}
            />
            {!isGoalView && !enableNxOnlyFeature && (
              <ButtonWithTooltip
                disabled={
                  (!ability.canCreateNewRecord || !ability.canSaveRecord) && !ability.canCreateSalescoreObjectRecord
                }
                showTooltip={
                  (!ability.canCreateNewRecord || !ability.canSaveRecord) && !ability.canCreateSalescoreObjectRecord
                }
                tooltipTitle={t(`権限がないため、行追加はできません。`)}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                onClick={async () => {
                  await addRootRecord({ rowIndex: -1 })
                  moveToRowIndexAndEditModeMutation(0)
                }}
                loading={savingFlag.isTrue}
                // type="primary"
                icon={<PlusOutlined />}
              >
                {t(`行追加`)}
              </ButtonWithTooltip>
            )}
            {!isSharedLinkPresetName(presetName) && hasChangeToViewSchema && (
              <ButtonWithTooltip
                disabled={!ability.canUpdate || !hasChangeToViewSchema}
                showTooltip={!ability.canUpdate}
                tooltipTitle={t(`権限がないため、設定の変更を破棄できません。`)}
                onClick={() => {
                  // presetNameがundefinedの場合(すなわち選択中のpresetを削除した時)、
                  // presetの先頭を表示する仕様だが、currentSavedViewのconfigは削除したpresetの設定が元となっているため
                  // 復元対象を先頭presetのconfigにしてあげる必要がある
                  if (
                    presetName === undefined &&
                    currentSavedView.config.type === 'sheet' &&
                    currentSavedView.config.presets?.[0] !== undefined
                  ) {
                    setConfig(
                      attachMetricToConfigFromFormula({
                        ...currentSavedView.config,
                        ...currentSavedView.config.presets[0],
                      }),
                    )
                  } else {
                    setConfig(attachMetricToConfigFromFormula(currentSavedView.config))
                  }
                  setHasChangeToViewSchema(false)
                }}
                icon={<DeleteOutlined />}
                danger
              >
                {t(`設定変更を破棄`)}
              </ButtonWithTooltip>
            )}
            {!isSharedLinkPresetName(presetName) &&
              (hasChangeToViewSchema || isSavingSettings || viewsContext.isSaveConfigButtonActive === true) && (
                <ButtonWithTooltip
                  disabled={
                    !ability.canUpdate || (!hasChangeToViewSchema && viewsContext.isSaveConfigButtonActive !== true)
                  }
                  showTooltip={!ability.canUpdate}
                  tooltipTitle={t(`権限がないため、保存はできません。`)}
                  onClick={() => {
                    setConfig((previous) => {
                      if (
                        previous.type !== 'sheet' ||
                        previous.presets === undefined ||
                        previous.presets.length === 0
                      ) {
                        return previous
                      }
                      return {
                        ...previous,
                        presets: previous.presets.map((preset) =>
                          preset.name === presetName
                            ? {
                                ...preset,
                                tree: previous.tree,
                                fields: previous.fields,
                                filterTree: previous.filterTree,
                                sorters: previous.sorters,
                                meta: previous.meta,
                              }
                            : preset,
                        ),
                      }
                    })
                    // eslint-disable-next-line sonarjs/todo-tag
                    // TODO: view.configを使うことが不適切ではないか。探してconfigを使用するように変更すること。
                    setView((previous) => ({ ...previous, config })) //「設定を保存」で view.config を最新化
                    setIsSavingSettings(true)
                  }}
                  loading={isSavingSettings}
                  type="primary"
                  icon={<SaveOutlined />}
                >
                  {t(`設定を保存`)}
                </ButtonWithTooltip>
              )}
            {viewsContext.prevView?.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX) !== true && (
              <Badge count={syncEnabled ? 0 : <LockTwoTone />}>
                <ButtonWithTooltip
                  disabled={
                    (!ability.canSaveRecord && !ability.canEditSalescoreObjectRecord) ||
                    changes.length === 0 ||
                    isPasting
                  }
                  showTooltip={(!ability.canSaveRecord && !ability.canEditSalescoreObjectRecord) || isPasting}
                  tooltipTitle={
                    isPasting
                      ? t(`ペースト処理中のため保存できません、完了までお待ちください`)
                      : t(`権限がないため、保存はできません。`)
                  }
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                  onClick={async () => {
                    if (syncEnabled) {
                      try {
                        await recordsAndChangesMutation.onSave(setRecordSaveErrors)
                      } catch (error) {
                        // とりあえずmessageを持つことだけ担保できればOK
                        const errorSchema = z.object({
                          message: z.string(),
                        })
                        const parsed = errorSchema.safeParse(error)
                        if (parsed.success) {
                          // NOTE: シート書き込み時のエラーは専用アラート(recordSaveErrorAlert)で知らせる
                          const [errorSummary] = parsed.data.message.split('\n')
                          showRecordSaveErrorsAlert(errorSummary)
                          return
                        }
                        message.error(t('エラーが発生しました。'))
                      }
                    } else {
                      modal.confirm(buildSyncUserPlanNotFoundModalProperties())
                    }
                  }}
                  loading={savingFlag.isTrue}
                  type="primary"
                  icon={<SaveOutlined />}
                >
                  {t(`保存`)}
                </ButtonWithTooltip>
              </Badge>
            )}
          </Space>
        </Row>
      </Row>
      <ViewUIErrorDrawer
        open={errorsDrawerOpen}
        onClose={() => {
          setErrorsDrawerOpen(false)
        }}
        recordSaveErrors={recordSaveErrors}
        columns={columns}
        compiledConfig={compiledConfig}
        view={view}
        ability={ability}
      />
    </div>
  )
}

const ColumnVisiblityItem = ({
  column,
  setHasChangeToViewSchema,
}: {
  column: RSheetColumn<ViewQueryRecordNode>
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const setUiColumn = useSetUiColumn()
  return (
    <div
      className="flex max-w-xs flex-nowrap justify-between gap-2"
      onClick={() => {
        const { field } = column
        if (field === undefined) {
          return
        }
        setUiColumn({
          field,
          column: {
            visible: column.visible !== true,
          },
          onSetConfig: () => {
            setHasChangeToViewSchema(true)
          },
        })
      }}
    >
      <span className="contents">{column.title}</span>
      {column.visible === true ? <EyeOutlined /> : <EyeInvisibleOutlined className="text-slate-400" />}
    </div>
  )
}
